import { useMemo, type ReactNode } from "react";

import { useDndContext, useDroppable } from "@dnd-kit/core";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import type { IScoringRuleDto } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import LeadScoreChip from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/LeadScoreChip/LeadScoreChip";
import { orderRulesSelectedFirst } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringBoardUtils";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";

import { DraggableScoringRuleCard } from "./DraggableScoringRuleCard";
import type { ScoringBoardSharedProps } from "./scoringBoardTypes";
import { ScoringRuleCard } from "./ScoringRuleCard";
import { ScoringRuleDropZone } from "./ScoringRuleDropZone";
import {
    StyledColumnDroppableArea,
    StyledColumnEmptyState,
    StyledColumnHeader,
    StyledColumnRulesContent,
    StyledColumnRulesCount,
    StyledScoringPriorityColumn,
} from "./styles";

export type ScoringPriorityColumnProps = ScoringBoardSharedProps & {
    priority: LeadPriorityScore;
    rules: readonly IScoringRuleDto[];
    onAddRule: () => void;
    onEditRule: (ruleIndex: number) => void;
    onDeleteRule: (ruleIndex: number) => void;
    addRuleControl?: ReactNode;
    hoveredDropPriority?: LeadPriorityScore | null;
};

type ScoringRuleDragData = {
    sourcePriority: LeadPriorityScore;
    ruleIndex: number;
    ruleId: string;
};

export const ScoringPriorityColumn = ({
    priority,
    rules,
    filtersList,
    signalById,
    onAddRule,
    onEditRule,
    onDeleteRule,
    addRuleControl,
    getRuleScope,
    canEditRule,
    getRuleUsage,
    selectedRuleId = null,
    selectedRuleIds,
    pinSelectedFirst = false,
    showUsedBadgeForSelected = false,
    onSelect,
    getSelectionDisabledReason,
    onMoveRule,
    hoveredDropPriority = null,
    automationPrefix = "scoring-board",
}: ScoringPriorityColumnProps) => {
    const { translate } = useTranslate();
    const isSelectable = onSelect !== undefined;
    const isDragEnabled = onMoveRule !== undefined && !isSelectable;
    const { active } = useDndContext();
    const { setNodeRef, isOver } = useDroppable({ id: priority });

    const effectiveSelectedRuleIds = useMemo(
        () => selectedRuleIds ?? (selectedRuleId ? new Set([selectedRuleId]) : undefined),
        [selectedRuleIds, selectedRuleId],
    );

    const orderedRules = useMemo(
        () => orderRulesSelectedFirst(rules, pinSelectedFirst ? effectiveSelectedRuleIds : undefined),
        [rules, pinSelectedFirst, effectiveSelectedRuleIds],
    );

    const activeDragData = active?.data.current as ScoringRuleDragData | undefined;
    const isDraggingFromOtherColumn =
        activeDragData?.sourcePriority !== undefined && activeDragData.sourcePriority !== priority;
    const isActiveDropTarget = isDraggingFromOtherColumn && (hoveredDropPriority === priority || isOver);
    const showDropZone = isDraggingFromOtherColumn;
    const shouldBlurCards = isActiveDropTarget;

    const renderRuleCard = (rule: IScoringRuleDto, index: number) => {
        const ruleId = rule.id;

        if (!ruleId) {
            return null;
        }

        const isEditable = canEditRule ? canEditRule(rule) : true;
        const cardProps = {
            rule,
            filtersList,
            signalById,
            scope: getRuleScope?.(rule),
            usedByPlaybookNames: getRuleUsage?.(rule) ?? EMPTY_ARRAY,
            showUsedBadgeForSelected,
            onEdit: isEditable ? () => onEditRule(index) : undefined,
            onDelete: isEditable ? () => onDeleteRule(index) : undefined,
            selection: isSelectable
                ? {
                      isSelected: effectiveSelectedRuleIds?.has(ruleId) ?? false,
                      onSelect: () => onSelect(ruleId, priority),
                      disabledReason: getSelectionDisabledReason?.(rule),
                  }
                : undefined,
        };

        if (isDragEnabled && isEditable) {
            return (
                <DraggableScoringRuleCard
                    key={ruleId}
                    ruleId={ruleId}
                    sourcePriority={priority}
                    ruleIndex={index}
                    isDraggable
                    {...cardProps}
                />
            );
        }

        return (
            <ScoringRuleCard
                key={ruleId}
                {...cardProps}
            />
        );
    };

    return (
        <StyledScoringPriorityColumn data-automation={`${automationPrefix}.column.${priority}`}>
            <StyledColumnHeader>
                <Flex
                    alignItems="center"
                    gap="8px"
                    minWidth="0"
                >
                    <LeadScoreChip
                        score={priority}
                        isClickable={false}
                    />
                    <StyledColumnRulesCount data-automation={`${automationPrefix}.column.${priority}.count`}>
                        {translate(
                            rules.length === 1
                                ? "si.pages.score_settings.board.column.rules_count.single"
                                : "si.pages.score_settings.board.column.rules_count.plural",
                            { count: rules.length },
                        )}
                    </StyledColumnRulesCount>
                </Flex>
                <Flex flexShrink={0}>
                    {addRuleControl ?? (
                        <Button
                            type="primary"
                            onClick={onAddRule}
                            dataAutomation={`${automationPrefix}.column.${priority}.add-rule`}
                        >
                            {`+ ${translate("si.pages.score_settings.board.column.add_rule")}`}
                        </Button>
                    )}
                </Flex>
            </StyledColumnHeader>

            <StyledColumnDroppableArea
                ref={setNodeRef}
                $isActiveDropTarget={isActiveDropTarget}
                data-automation={`${automationPrefix}.column.${priority}.rules-area`}
            >
                {rules.length === 0 ? (
                    <>
                        {showDropZone ? <ScoringRuleDropZone priority={priority} /> : null}
                        {!showDropZone ? (
                            <StyledColumnEmptyState data-automation={`${automationPrefix}.column.${priority}.empty`}>
                                {translate("si.pages.score_settings.board.column.empty")}
                            </StyledColumnEmptyState>
                        ) : null}
                    </>
                ) : (
                    <>
                        <StyledColumnRulesContent
                            $isBlurred={shouldBlurCards}
                            data-automation={`${automationPrefix}.column.${priority}.rules`}
                        >
                            {orderedRules.map(({ rule, index }) => renderRuleCard(rule, index))}
                        </StyledColumnRulesContent>
                        {showDropZone ? <ScoringRuleDropZone priority={priority} /> : null}
                    </>
                )}
            </StyledColumnDroppableArea>
        </StyledScoringPriorityColumn>
    );
};
